import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import {
  abmelden as apiAbmelden,
  hauskopf as holeHauskopf,
  ich,
  type Benutzer,
  type Hauskopf,
} from '../api/anmeldung';
import { meldeSitzungsende, schluesselAblage } from '../api/client';
import { ApiFehler } from '../api/fehler';
import { verwirfAlle } from '../entwurf';
import { Anmeldemaske } from './Anmeldemaske';
import { Passwortwechsel } from './Passwortwechsel';
import { ZweiterFaktor } from './ZweiterFaktor';

/**
 * Die Zustaende, in denen die Oberflaeche stehen kann.
 *
 * Als ausdrueckliche Aufzaehlung und nicht als Sammlung von Wahrheitswerten:
 * `angemeldet && !wechselFaellig && !faktorFehlt` laesst Zustaende zu, die es
 * nicht geben darf, und man sieht ihnen nicht an, welche.
 */
export type Zustand =
  | { readonly art: 'pruefe' }
  | { readonly art: 'anmeldung'; readonly meldung?: string }
  | {
      readonly art: 'faktor';
      readonly anmeldename: string;
      readonly passwort: string;
      readonly grund?: string;
    }
  | { readonly art: 'wechsel'; readonly benutzer: Benutzer }
  | { readonly art: 'angemeldet'; readonly benutzer: Benutzer };

/**
 * DIE ANMELDUNG — EINMAL FUER ZWEI OBERFLAECHEN.
 *
 * ============================================================================
 * WARUM SIE AUS App.tsx HERAUSGELOEST IST
 * ============================================================================
 *
 * Seit dem 11.09.2026 gibt es zwei Einstiegspunkte: das CRM unter
 * `index.html` und den Trainerbereich unter `trainer.html` (RES-27, RES-30).
 * Beide brauchen denselben Weg hinein — Passwort, zweiter Faktor, erzwungener
 * Wechsel, Sitzungsende, Abmelden —, und dieser Weg ist der heikelste Teil
 * der Oberflaeche: BEN-06 (der Wechsel sperrt alles andere), BEN-07 (Leerlauf
 * und Fernabmeldung), SYS-05 (zweiter Faktor), NFA-07 (die Ansage beim
 * Maskenwechsel).
 *
 * ZWEIMAL GESCHRIEBEN WAERE ER EINMAL ZU VIEL. Die zweite Fassung haette
 * gegolten, bis jemand an der ersten etwas richtigstellt — und gemerkt haette
 * es niemand, weil beide Fassungen in normalen Faellen dasselbe tun. Deshalb
 * steht der Weg hier, und die Aufrufer geben nur zwei Dinge mit: die
 * Seitentitel und was nach der Anmeldung kommt.
 *
 * WAS SIE NICHT TUT: Sie entscheidet NICHT, wer hinein darf. Jede Oberflaeche
 * prueft selbst, ob das angemeldete Konto zu ihr gehoert — das CRM weist
 * Dienstleisterkonten ab, der Trainerbereich weist Mitarbeiterkonten ab. Die
 * Pruefung gehoert dorthin, weil nur die Oberflaeche weiss, fuer wen sie
 * gebaut ist.
 */
export function Anmeldehuelle({ titel: titelJeZustand, angemeldet }: {
  /** Der Seitentitel je Zustand — er sagt Hilfsmitteln, wo man gelandet ist. */
  readonly titel: Record<Zustand['art'], string>;
  /**
   * Was nach der Anmeldung kommt.
   *
   * Als Funktion und nicht als Kind: Der Benutzer entsteht erst hier, und die
   * Abmeldung gehoert der Huelle. Ein Kind muesste beides von aussen bekommen,
   * und aussen gibt es es noch nicht.
   */
  readonly angemeldet: (benutzer: Benutzer, abmelden: () => void) => JSX.Element;
}): JSX.Element {
  const [zustand, setzeZustand] = useState<Zustand>({ art: 'pruefe' });
  const titel = useRef<HTMLHeadingElement>(null);
  const ersteAnzeige = useRef(true);

  /**
   * Jeder Maskenwechsel wird angesagt (NFA-07, WCAG 2.4.3 und 4.1.3).
   *
   * Ohne das passiert Folgendes: Der Anwender drueckt "Anmelden", der Knopf
   * wird gesperrt, der Browser wirft den Fokus dabei auf den Seitenrumpf. Die
   * Anmeldung gelingt, der gesamte Baum wird ausgetauscht — und ein
   * Vorleseprogramm sagt NICHTS. Der Benutzer steht am Anfang eines voellig
   * anderen Dokuments und weiss nicht, dass sich etwas geaendert hat.
   *
   * Zwei Mittel dagegen: der Seitentitel, den Hilfsmittel beim Wechsel
   * vorlesen, und der Fokus auf die Ueberschrift der neuen Maske — von dort
   * liest sich der Rest der Seite der Reihe nach.
   *
   * EINE AUSNAHME: DIE ANMELDUNG OHNE MELDUNG.
   *
   * Dort gehoert der Fokus in das Feld "Anmeldename" — wer die Seite
   * oeffnet, will tippen, nicht lesen. Die Maske setzt ihn selbst; ohne diese
   * Ausnahme nahm ihn die Ueberschrift wieder weg, weil Effekte der
   * Elternkomponente NACH denen der Kinder laufen. Genau das ist im Betrieb
   * aufgefallen.
   *
   * MIT Meldung bleibt es bei der Ueberschrift: Wer mit "Ihre Sitzung ist
   * beendet" zurueckkommt, soll die Erklaerung hoeren und nicht in einem Feld
   * stehen, das sie ueberspringt.
   */
  useEffect(() => {
    document.title = titelJeZustand[zustand.art];
    if (ersteAnzeige.current) {
      ersteAnzeige.current = false;
      return;
    }
    if (zustand.art === 'anmeldung' && zustand.meldung === undefined) return;
    titel.current?.focus();
  }, [zustand, titelJeZustand]);

  /**
   * Beim Laden pruefen, ob der Schluessel aus der Ablage noch gilt.
   *
   * Ohne diesen Schritt landete jeder nach einem Neuladen wieder in der
   * Anmeldung, obwohl seine Sitzung laeuft.
   */
  useEffect(() => {
    if (schluesselAblage.lesen() === null) {
      setzeZustand({ art: 'anmeldung' });
      return;
    }
    void (async () => {
      try {
        const auskunft = await ich();
        setzeZustand({ art: 'angemeldet', benutzer: auskunft.benutzer });
      } catch (ursache) {
        // Die Meldung des Fehlers wird durchgereicht statt verworfen. Ein
        // stiller Wechsel zur Anmeldung liesse den Anwender raten, warum er
        // wieder davorsteht (ERR-01, ERR-02).
        const f = ursache as ApiFehler;
        setzeZustand((vorher) => {
          // Der Rueckruf aus dem Client hat bei einer abgelaufenen Sitzung
          // bereits eine Meldung gesetzt — sie ist die genauere.
          if (vorher.art === 'anmeldung' && vorher.meldung !== undefined) return vorher;
          return {
            art: 'anmeldung',
            meldung:
              f instanceof ApiFehler
                ? `${f.meldung.wasGeschah} ${f.meldung.naechsterSchritt}`
                : 'Bitte melden Sie sich an.',
          };
        });
      }
    })();
  }, []);

  /** Die API meldet, dass eine Sitzung nicht mehr gilt (BEN-07). */
  useEffect(() => {
    meldeSitzungsende(() => {
      // Die Entwuerfe bleiben ausdruecklich STEHEN. Ein Sitzungsende nach
      // Untaetigkeit ist kein Abmelden durch den Anwender, sondern genau der
      // unerwartete Verlust, gegen den ERR-11 den Zwischenspeicher fordert.
      // Passwoerter liegen ohnehin nie darin (entwurf.ts).
      setzeZustand({
        art: 'anmeldung',
        meldung: 'Ihre Sitzung ist beendet. Bitte melden Sie sich erneut an.',
      });
    });
  }, []);

  /**
   * CRM-153: Firmierung und Kontakt aus den Geschäftsdaten — EINMAL geholt,
   * sobald die Anmeldung zum ersten Mal zu sehen ist.
   *
   * Nicht beim Start: Wer eine laufende Sitzung hat, sieht die Anmeldung nie.
   * Nicht bei jedem Wechsel zurück zur Maske: Der Weg zählt in den Korb der
   * Anmeldung (WEB-03), und jeder Abruf wäre ein Versuch weniger.
   *
   * SCHEITERT DER ABRUF, BLEIBT ES BEIM BISHERIGEN: keine Firmierung, der
   * Satz mit der Administration. Die Anmeldung hängt nicht an diesem Kopf,
   * und eine Fehlermeldung über ihm verdeckte die, auf die es ankommt.
   */
  const [haus, setzeHaus] = useState<Hauskopf | undefined>(undefined);
  const hausGeholt = useRef(false);
  useEffect(() => {
    if (zustand.art !== 'anmeldung' || hausGeholt.current) return;
    hausGeholt.current = true;
    holeHauskopf().then(setzeHaus, () => undefined);
  }, [zustand.art]);

  const abmelden = useCallback(async (): Promise<void> => {
    try {
      await apiAbmelden();
    } finally {
      // Beim ausdruecklichen Abmelden gehen die Entwuerfe mit: Wer sich
      // abmeldet, uebergibt den Rechner moeglicherweise weiter.
      verwirfAlle();
      setzeZustand({ art: 'anmeldung', meldung: 'Sie sind abgemeldet.' });
    }
  }, []);

  switch (zustand.art) {
    case 'pruefe':
      return (
        <main className="maske">
          <h1 tabIndex={-1} ref={titel}>
            Anmeldung wird geprüft
          </h1>
          <p role="status">Einen Augenblick …</p>
        </main>
      );

    case 'anmeldung':
      return (
        <Anmeldemaske
          titelVerweis={titel}
          meldung={zustand.meldung}
          haus={haus}
          beiErfolg={(ergebnis) =>
            setzeZustand(
              ergebnis.passwortwechselFaellig
                ? { art: 'wechsel', benutzer: ergebnis.benutzer }
                : { art: 'angemeldet', benutzer: ergebnis.benutzer },
            )
          }
          beiFaktorFehlt={(anmeldename, passwort, grund) =>
            setzeZustand({
              art: 'faktor',
              anmeldename,
              passwort,
              grund: `${grund.meldung.wasGeschah} ${grund.meldung.auswirkung}`,
            })
          }
        />
      );

    case 'faktor':
      return (
        <ZweiterFaktor
          titelVerweis={titel}
          anmeldename={zustand.anmeldename}
          passwort={zustand.passwort}
          grund={zustand.grund}
          beiFertig={() =>
            setzeZustand({
              art: 'anmeldung',
              meldung:
                'Der zweite Faktor ist eingerichtet. Melden Sie sich jetzt mit dem Code aus der App an.',
            })
          }
          beiAbbruch={() =>
            setzeZustand({
              art: 'anmeldung',
              meldung:
                'Die Einrichtung wurde abgebrochen. Ohne eingerichteten zweiten Faktor ist keine Anmeldung möglich.',
            })
          }
        />
      );

    case 'wechsel':
      return (
        <Passwortwechsel
          titelVerweis={titel}
          anmeldename={zustand.benutzer.anmeldename}
          beiFertig={() =>
            setzeZustand({
              art: 'anmeldung',
              meldung:
                'Das Passwort ist gesetzt. Alle Sitzungen wurden beendet — bitte melden Sie sich neu an.',
            })
          }
          beiAbmelden={() => void abmelden()}
        />
      );

    case 'angemeldet':
      return angemeldet(zustand.benutzer, () => void abmelden());
  }
}
